if (!customElements.get('product-series-switcher')) {
  class ProductSeriesSwitcher extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.initCarousel();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.destroyCarousel();
    }

    initCarousel() {
      this.list = this.querySelector('.product-series-switcher__list');
      this.prevBtn = this.querySelector('[data-series-nav="prev"]');
      this.nextBtn = this.querySelector('[data-series-nav="next"]');
      if (!this.list) return;

      this.list.addEventListener('scroll', this.updateNav, { passive: true });
      this.list.addEventListener('scroll', this.onInfiniteScroll, { passive: true });
      this.prevBtn?.addEventListener('click', this.scrollPrev);
      this.nextBtn?.addEventListener('click', this.scrollNext);

      this.resizeObserver = new ResizeObserver(() => {
        if (this._infiniteReady && !this._didInitialJump) {
          this.jumpToRealSet();
          this._didInitialJump = true;
        }
        this.normalizeInfiniteScroll();
        this.updateNav();
      });
      this.resizeObserver.observe(this.list);

      this.setupInfiniteScroll();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.jumpToRealSet();
          this._didInitialJump = true;
          this.updateNav();
        });
      });
    }

    destroyCarousel() {
      this.list?.removeEventListener('scroll', this.updateNav);
      this.list?.removeEventListener('scroll', this.onInfiniteScroll);
      this.prevBtn?.removeEventListener('click', this.scrollPrev);
      this.nextBtn?.removeEventListener('click', this.scrollNext);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    setupInfiniteScroll() {
      if (!this.list || this.list.dataset.infinite === 'true') {
        this._infiniteReady = this.list?.dataset.infinite === 'true';
        this._setCount = parseInt(this.list?.dataset.setCount || '0', 10);
        this._didInitialJump = true;
        return;
      }

      const items = [...this.list.querySelectorAll(':scope > .product-series-switcher__list-item')];
      if (items.length < 2) {
        this._infiniteReady = false;
        return;
      }

      const prependFrag = document.createDocumentFragment();
      const appendFrag = document.createDocumentFragment();

      items.forEach((item) => {
        const pre = item.cloneNode(true);
        const post = item.cloneNode(true);
        [pre, post].forEach((clone) => {
          clone.dataset.clone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
        });
        prependFrag.appendChild(pre);
        appendFrag.appendChild(post);
      });

      this.list.prepend(prependFrag);
      this.list.append(appendFrag);
      this.list.dataset.infinite = 'true';
      this.list.dataset.setCount = String(items.length);
      this._infiniteReady = true;
      this._setCount = items.length;
      this._jumping = false;
      this._didInitialJump = false;
    }

    getSetWidth() {
      if (!this.list || !this._infiniteReady) return 0;
      return this.list.scrollWidth / 3;
    }

    jumpToRealSet() {
      if (!this.list || !this._infiniteReady) return;
      const setWidth = this.getSetWidth();
      if (!setWidth) return;
      this._jumping = true;
      this.list.style.scrollBehavior = 'auto';
      this.list.scrollLeft = setWidth;
      this._jumping = false;
    }

    normalizeInfiniteScroll() {
      if (!this.list || !this._infiniteReady || this._jumping) return;
      const setWidth = this.getSetWidth();
      if (!setWidth) return;

      const scrollLeft = this.list.scrollLeft;
      if (scrollLeft < setWidth) {
        this._jumping = true;
        this.list.style.scrollBehavior = 'auto';
        this.list.scrollLeft = scrollLeft + setWidth;
        this._jumping = false;
      } else if (scrollLeft >= setWidth * 2) {
        this._jumping = true;
        this.list.style.scrollBehavior = 'auto';
        this.list.scrollLeft = scrollLeft - setWidth;
        this._jumping = false;
      }
    }

    onInfiniteScroll = () => {
      this.normalizeInfiniteScroll();
    };

    getScrollAmount() {
      const item = this.list?.querySelector('.product-series-switcher__list-item');
      if (!item) return this.list?.clientWidth * 0.75 || 0;
      const styles = window.getComputedStyle(this.list);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    scrollPrev = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.list) return;
      this.list.scrollBy({ left: -this.getScrollAmount() * 2, behavior: 'smooth' });
    };

    scrollNext = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.list) return;
      this.list.scrollBy({ left: this.getScrollAmount() * 2, behavior: 'smooth' });
    };

    updateNav = () => {
      if (!this.list) return;

      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      const canScroll = maxScroll > 2;

      if (this._infiniteReady) {
        if (this.prevBtn) this.prevBtn.hidden = !canScroll;
        if (this.nextBtn) this.nextBtn.hidden = !canScroll;
        return;
      }

      const atStart = this.list.scrollLeft <= 2;
      const atEnd = this.list.scrollLeft >= maxScroll - 2;

      if (this.prevBtn) this.prevBtn.hidden = !canScroll || atStart;
      if (this.nextBtn) this.nextBtn.hidden = !canScroll || atEnd;
    };

    handleClick = (event) => {
      const link = event.target.closest('[data-product-url]');
      if (!link || !this.contains(link)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();

      if (link.classList.contains('is-active')) return;

      const url = link.getAttribute('data-product-url');
      if (!url) return;

      ProductSeriesSwitcher.navigate(url, true);
    };

    static loading = false;

    static reinjectScripts(container, skipRoot) {
      container.querySelectorAll('script').forEach((oldScriptTag) => {
        if (skipRoot?.contains(oldScriptTag)) return;
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    static setLoading(section, isLoading) {
      if (!section) return;
      const product = section.querySelector('.product');
      const breadcrumb = section.querySelector('.bear-breadcrumb');
      [product, breadcrumb].forEach((el) => {
        if (!el) return;
        el.classList.toggle('product-series-switcher--loading-target', isLoading);
      });
      section.classList.toggle('product-series-switcher--loading', isLoading);
    }

    static getStylesheetKey(link) {
      const href = link.getAttribute('href') || '';
      try {
        const url = new URL(href, window.location.origin);
        return url.pathname;
      } catch (error) {
        return href;
      }
    }

    static removeStaleProductModals(section) {
      const sectionId = section?.dataset?.section;
      if (!sectionId) return;

      document.querySelectorAll(`product-modal#ProductModal-${sectionId}`).forEach((modal) => {
        modal.remove();
      });
    }

    static replaceSectionKeepingSwitcher(section, newSection, preservedSwitcher) {
      newSection.querySelector('product-series-switcher')?.remove();
      ProductSeriesSwitcher.removeStaleProductModals(section);

      const list = preservedSwitcher?.querySelector('.product-series-switcher__list');
      const savedScrollLeft = list ? list.scrollLeft : 0;

      const existingStyleKeys = new Set(
        [...section.querySelectorAll('link[rel="stylesheet"]')].map((link) =>
          ProductSeriesSwitcher.getStylesheetKey(link)
        )
      );

      [...section.children].forEach((child) => {
        if (child === preservedSwitcher) return;
        if (child.matches?.('link[rel="stylesheet"]')) return;
        child.remove();
      });

      while (newSection.firstChild) {
        const node = newSection.firstChild;

        if (node.nodeType === 1 && node.matches('link[rel="stylesheet"]')) {
          const key = ProductSeriesSwitcher.getStylesheetKey(node);
          if (key && existingStyleKeys.has(key)) {
            const existing = [...section.querySelectorAll('link[rel="stylesheet"]')].find(
              (link) => ProductSeriesSwitcher.getStylesheetKey(link) === key
            );
            const nextHref = node.getAttribute('href');
            if (existing && nextHref && existing.getAttribute('href') !== nextHref) {
              existing.setAttribute('href', nextHref);
            }
            node.remove();
            continue;
          }
          if (key) existingStyleKeys.add(key);
        }

        section.appendChild(node);
      }

      if (preservedSwitcher) {
        const breadcrumb = section.querySelector('.bear-breadcrumb');
        if (breadcrumb && preservedSwitcher.nextElementSibling !== breadcrumb) {
          breadcrumb.before(preservedSwitcher);
        } else if (!breadcrumb) {
          const product = section.querySelector('.product');
          if (product && preservedSwitcher.nextElementSibling !== product) {
            product.before(preservedSwitcher);
          } else if (!product) {
            section.prepend(preservedSwitcher);
          }
        }
      }

      ProductSeriesSwitcher.reinjectScripts(section, preservedSwitcher);

      const restoreScroll = () => {
        if (!list) return;
        list.style.scrollBehavior = 'auto';
        list.scrollLeft = savedScrollLeft;
      };
      restoreScroll();
      requestAnimationFrame(() => {
        restoreScroll();
        requestAnimationFrame(restoreScroll);
      });
    }

    static updateActiveItem(switcher, url) {
      if (!switcher || !url) return;

      const targetPath = new URL(url, window.location.origin).pathname;

      switcher.querySelectorAll('[data-product-url]').forEach((link) => {
        const linkPath = new URL(link.getAttribute('data-product-url'), window.location.origin).pathname;
        const isActive = linkPath === targetPath;
        const isClone = Boolean(link.closest('[data-clone="true"]'));
        link.classList.toggle('is-active', isActive);
        if (isActive && !isClone) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      switcher.updateNav?.();
    }

    static async navigate(url, push) {
      const section = document.querySelector('section[id^="MainProduct-"]');
      if (!section || ProductSeriesSwitcher.loading) return;

      ProductSeriesSwitcher.loading = true;
      ProductSeriesSwitcher.setLoading(section, true);

      const preservedSwitcher = section.querySelector('product-series-switcher');

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch product: ${response.status}`);

        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newSection = responseHTML.querySelector('section[id^="MainProduct-"]');
        if (!newSection) throw new Error('Main product section not found');

        ProductSeriesSwitcher.replaceSectionKeepingSwitcher(section, newSection, preservedSwitcher);
        ProductSeriesSwitcher.updateActiveItem(preservedSwitcher, url);

        const titleEl = responseHTML.querySelector('title');
        if (titleEl) document.title = titleEl.textContent;

        if (push) {
          history.pushState({ seriesSwitcher: true }, '', url);
        }

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();
      } catch (error) {
        console.error(error);
        window.location.href = url;
      } finally {
        ProductSeriesSwitcher.loading = false;
        const currentSection = document.querySelector('section[id^="MainProduct-"]');
        ProductSeriesSwitcher.setLoading(currentSection, false);
      }
    }
  }

  customElements.define('product-series-switcher', ProductSeriesSwitcher);

  if (!window.__productSeriesSwitcherPopstateBound) {
    window.__productSeriesSwitcherPopstateBound = true;
    window.addEventListener('popstate', () => {
      if (!window.location.pathname.includes('/products/')) {
        window.location.reload();
        return;
      }
      if (!document.querySelector('section[id^="MainProduct-"]')) return;
      ProductSeriesSwitcher.navigate(window.location.href, false);
    });
  }
}
